// General
import "./upgrade-promo-dialog.scss";
import { useState, useEffect, forwardRef } from "react";
// Services
import {
  useClaimPromoCodeMutation,
  useLazyGetProductsListQuery,
} from "../../../../services/data.service";
// Redux
import { useSelector, useDispatch } from "react-redux";
import {
  updateSuccessToast,
  updateErrorToast,
} from "../../../../redux/store/toastStore";
import { updateUpgradePromoDialog } from "../../../../redux/store/dialogStore";
// react-gtm-module
import TagManager from "react-gtm-module";
// Material UI
import { Dialog, TextField, Slide, useMediaQuery } from "@mui/material";
// i18next
import { useTranslation } from "react-i18next";
// Components
import Spinner from "../../elements/spinner/spinner";

const UpgradePromoDialog = () => {
  // API variables
  const [
    claimPromoCode,
    {
      data: claimPromoCodeData,
      error: claimPromoCodeErrorData,
      isLoading: claimPromoCodeLoading,
      isSuccess: claimPromoCodeSuccess,
      isError: claimPromoCodeError,
    },
  ] = useClaimPromoCodeMutation();
  const [
    getProductsList,
    {
      data: getProductsListData,
      error: getProductsListErrorData,
      isFetching: getProductsListFetching,
      isLoading: getProductsListLoading,
      isSuccess: getProductsListSuccess,
      isError: getProductsListError,
    },
  ] = useLazyGetProductsListQuery();

  // General variables
  const [promoCode, setPromoCode] = useState("");

  // Redux variables
  const upgradePromoDialog = useSelector(
    (state) => state.dialog.upgradePromoDialog
  );
  const utmSource = useSelector((state) => state.tracking.utmSource);
  const dispatch = useDispatch();

  // MUI variables
  const isMobile = useMediaQuery("(max-width: 720px)");

  // i18next variables
  const { t } = useTranslation();

  // Lifecycle | Mounted
  useEffect(() => {
    if (!upgradePromoDialog) return;

    TagManager.dataLayer({
      dataLayer: {
        event: "PWA-UpgradePromo-Dialog",
      },
    });
  }, [upgradePromoDialog]);

  // Lifecycle | Check for update | claimPromoCode API Response
  useEffect(() => {
    if (claimPromoCodeLoading) {
    } else if (claimPromoCodeSuccess) {
      switch (claimPromoCodeData?.status) {
        case 0:
          getProductsList(`?product_type=premium`, false);
          const successToastObj = {
            message: t("upgrade.promo_code_applied"),
            autoClose: 3000,
          };
          dispatch(updateSuccessToast(successToastObj));
          onCloseDialog();
          break;
        case 1:
          const errorToastObj = {
            message: claimPromoCodeData?.message,
            autoClose: 3000,
          };
          dispatch(updateErrorToast(errorToastObj));
          break;
        default:
          break;
      }
    } else if (claimPromoCodeError) {
    }
  }, [claimPromoCodeLoading, claimPromoCodeSuccess, claimPromoCodeError]);

  // Event Handlers | Button
  const onSubmit = () => {
    if (!isFormValid()) return;
    const obj = {
      code: promoCode?.trim(),
      product_type: "premium",
    };
    claimPromoCode(obj);
  };

  // Event Handlers | MUI Dialog
  const onCloseDialog = () => {
    dispatch(updateUpgradePromoDialog(false));
  };

  // Event Handlers | MUI TextField
  const onPromoCodeChange = (event) => {
    setPromoCode(event.target.value);
  };

  // Utility Functions
  const isFormValid = () => {
    return promoCode?.trim() !== "";
  };

  return (
    <Dialog
      className={
        isMobile ? "custom-bottom-sheet-dialog" : "custom-radius10-dialog"
      }
      fullScreen={isMobile ? true : false}
      open={upgradePromoDialog}
      TransitionComponent={Transition}
      onClose={onCloseDialog}
    >
      <div id="upgrade-promo-dialog">
        <div className="padding-container">
          <div className="promo-text-field-container">
            <div className="enter-code-label">{t("upgrade.enter_code")}</div>

            <div style={{ padding: "2px" }} />

            <div className="enter-code-description">
              {t("upgrade.enter_code_description")}
            </div>

            <div style={{ padding: "8px" }} />

            <TextField
              className="promo-form-field custom-text-field"
              value={promoCode}
              onChange={onPromoCodeChange}
              placeholder={t("upgrade.promo_code_placeholder")}
              helperText={claimPromoCodeData?.message || ""}
              error={claimPromoCodeData?.status === -1 ? true : false}
              variant="outlined"
            />
          </div>

          <div style={{ padding: "20px" }} />

          <div className="buttons-container">
            <div
              className={`submit-button ${
                isFormValid() ? "" : "disabled-button"
              }`}
              onClick={onSubmit}
            >
              {claimPromoCodeLoading ? (
                <Spinner size={20} isPadding={false} color={"white-spinner"} />
              ) : (
                t("common.use_now")
              )}
            </div>
          </div>
        </div>
      </div>
    </Dialog>
  );
};

export default UpgradePromoDialog;

// Render Functions | MUI Dialog | Will not work if inside of the same render as the Dialog
const Transition = forwardRef(function Transition(props, ref) {
  return <Slide direction="up" ref={ref} {...props} />;
});
